import { useRef, useCallback } from "react";

import kutil from "@/modules/kutil";
import { useSSE, type SseErrorEvent } from "@modules/sse";

import { useAuthStore } from "@user/store/auth_store";
import { usePlcStore, type MFCStatus, type SCStatus, type RVStatus, type CVStatus, type WorkDetail } from "@/pages/user/store/plc_store";

function PlcManager() {
  const initialized = useRef(false);
  const isPageReloading = useRef(false);
  const uncommittedMFCStatuses = useRef<MFCStatus[]>([]);
  const uncommittedWorkDetails = useRef<(WorkDetail | WorkDetail[])[]>([]);
  const uncommittedSCStatuses = useRef<SCStatus[]>([]);
  const uncommittedRVStatuses = useRef<RVStatus[]>([]);
  const uncommittedCVStatuses = useRef<CVStatus[]>([]);

  const sessionId = useAuthStore((s) => s.sessionId);

  const reloadPage = useCallback(async (
    { message, delayMs = 0, onBeforeReload } : {message: string, delayMs?: number, onBeforeReload?: () => void}
  ) => {
    if (isPageReloading.current) return;
    isPageReloading.current = true;
    await kutil.alert(message);
    onBeforeReload?.();
    await kutil.sleep(delayMs);
    window.location.reload();
  }, []);

  // MFC 상태
  const onSSEMessageMFC = useCallback((msg: MessageEvent) => {
    try {
      const status: MFCStatus = JSON.parse(msg.data);
      if (!initialized.current) {
        uncommittedMFCStatuses.current.push(status);
      } else {
        usePlcStore.getState().updateMFCStatus(status);
      }
    } catch {
      console.error("SSE 메시지 처리 실패:", msg.data);
    }
  }, []);

  // WorkDetail
  const onSSEMessageWorkDetail = useCallback((msg: MessageEvent) => {
    try {
      const result = JSON.parse(msg.data);
      const isFull: boolean = result.isFull;
      const infos: WorkDetail[] = result.infos;
      console.log(infos);
      if (!initialized.current) {
        if (isFull) {
          uncommittedWorkDetails.current = [infos];
        } else {
          uncommittedWorkDetails.current.push(...infos);
        }
      } else {
        if (isFull) {
          usePlcStore.getState().updateWorkDetails(infos);
        } else {
          infos.forEach(info => {
            usePlcStore.getState().updateWorkDetail(info);
          });
        }
      }
    } catch {
      console.error("SSE 메시지 처리 실패:", msg.data);
    }
  }, []);

  // STC 상태
  const onSSEMessageSC = useCallback((msg: MessageEvent) => {
    try {
      const status: SCStatus = JSON.parse(msg.data);
      console.log(status);
      if (!initialized.current) {
        uncommittedSCStatuses.current.push(status);
      } else {
        usePlcStore.getState().updateSCStatus(status);
      }
    } catch {
      console.error("SSE 메시지 처리 실패:", msg.data);
    }
  }, []);

  // RGV 상태
  const onSSEMessageRV = useCallback((msg: MessageEvent) => {
    try {
      const status: RVStatus = JSON.parse(msg.data);
      console.log(status);
      if (!initialized.current) {
        uncommittedRVStatuses.current.push(status);
      } else {
        usePlcStore.getState().updateRVStatus(status);
      }
    } catch {
      console.error("SSE 메시지 처리 실패:", msg.data);
    }
  }, []);

  // CV 상태
  const onSSEMessageCV = useCallback((msg: MessageEvent) => {
    try {
      const statuses: CVStatus[] = JSON.parse(msg.data);
      console.log(statuses);
      if (!initialized.current) {
        uncommittedCVStatuses.current.push(...statuses);
      } else {
        statuses.forEach(status => {
          usePlcStore.getState().updateCVStatus(status);
        });
      }
    } catch {
      console.error("SSE 메시지 처리 실패:", msg.data);
    }
  }, []);

  // 초기화(전체상태)
  const onSSEMessageAll = useCallback(async (msg: MessageEvent) => {
    try {
      const { mfcStatus, workDetails, scStatuses, rvStatuses, cvStatuses } = JSON.parse(msg.data);
      console.log("초기화(전체상태)", workDetails, scStatuses, rvStatuses, cvStatuses);
  
      usePlcStore.getState().updateMFCStatus(mfcStatus);
      uncommittedMFCStatuses.current.forEach((info) => {
        usePlcStore.getState().updateMFCStatus(info);
      });
      uncommittedMFCStatuses.current = [];

      usePlcStore.getState().updateWorkDetails(workDetails);
      uncommittedWorkDetails.current.forEach((info) => {
        if (Array.isArray(info)) {
          usePlcStore.getState().updateWorkDetails(info);
        } else {
          usePlcStore.getState().updateWorkDetail(info);
        }
      });
      uncommittedWorkDetails.current = [];

      scStatuses.forEach((status: SCStatus) => {
        usePlcStore.getState().updateSCStatus(status);
      });
      uncommittedSCStatuses.current.forEach((status) => {
        usePlcStore.getState().updateSCStatus(status);
      });
      uncommittedSCStatuses.current = [];

      rvStatuses.forEach((status: RVStatus) => {
        usePlcStore.getState().updateRVStatus(status);
      });
      uncommittedRVStatuses.current.forEach((status) => {
        usePlcStore.getState().updateRVStatus(status);
      });
      uncommittedRVStatuses.current = [];

      cvStatuses.forEach((status: CVStatus) => {
        usePlcStore.getState().updateCVStatus(status);
      });
      uncommittedCVStatuses.current.forEach((status) => {
        usePlcStore.getState().updateCVStatus(status);
      });
      uncommittedCVStatuses.current = [];

      initialized.current = true;
    } catch {
      console.error("초기화 실패:", msg.data);
      await reloadPage({
        message: "초기화 실패. 다시 시도합니다.\n확인을 누르면 3초 후 새로고침됩니다.",
        delayMs: 3000
      });
    }
  }, [reloadPage]);

  // SSE 연결시
  const onSSEOpen = useCallback((_event: Event) => {
    initialized.current = false;
    uncommittedWorkDetails.current = [];
    uncommittedSCStatuses.current = [];
    uncommittedRVStatuses.current = [];
    uncommittedCVStatuses.current = [];
  }, []);

  // SSE 로그인 오류시 페이지 새로고침
  const onSSEError = useCallback(async (err: SseErrorEvent) => {
    if (err.code === 400 || err.code === 401 || err.code === 403) {
      await reloadPage({
        message: "로그인이 필요합니다.",
        onBeforeReload: () => kutil.removeRefreshToken()
      });
    }
    usePlcStore.getState().updateMFCStatus({
      isStarted: false
    });
    console.log(err);
  }, [reloadPage]);

  useSSE({
    url: "/api/sse/subscribe?eventGroup=plc-statuses&eventKey=",
    sessionId,
    enabled: !!sessionId,
    onOpen: onSSEOpen,
    onError: onSSEError,
    messageHandlers: {
      "plc-statuses:init-all": onSSEMessageAll,
      "plc-statuses:mfc": onSSEMessageMFC,
      "plc-statuses:work-detail": onSSEMessageWorkDetail,
      "plc-statuses:sc": onSSEMessageSC,
      "plc-statuses:rv": onSSEMessageRV,
      "plc-statuses:cv": onSSEMessageCV,
    },
  });

  return null;
}

export default PlcManager;
